import { PackBox, AddPackBox } from "@/types/packing";
import { Form, InputNumber, Input, Modal } from "antd";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import usePackOrderDetail from "../hooks";
import { numFormmater } from "@/pages/Requirement/util";
import SelectPackageType from "@/components/Selector/SelectPackageType";
import { PackOrderContext } from "../context";

interface BaseProps {
  orderId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (newBox: PackBox) => void;
}
interface BoxAddProps {
  formType: "ADD";
  box: null;
};
interface BoxEditProps {
  formType: "EDIT";
  box: PackBox;
};
type BoxAddOrEditProps = (BoxAddProps | BoxEditProps) & BaseProps;

const BoxAddOrEdit: FC<BoxAddOrEditProps> = ({ formType, open, orderId, box, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { orderData } = useContext(PackOrderContext);
  const { addBox, loading, editBox } = usePackOrderDetail();
  const [form] = Form.useForm<AddPackBox>();
  useEffect(() => {
    if (open && formType === "EDIT") {
      form.setFieldsValue({
        boxNumber: box.boxNumber,
        lengthMm: box.lengthMm,
        widthMm: box.widthMm,
        heightMm: box.heightMm,
        grossWeightG: box.grossWeightG,
        netWeightG: box.netWeightG,
        packageType: box.packageType,
        marksNumbers: box.marksNumbers,
        remark: box.remark,
      });
    } else if (open && formType === "ADD") {
      initAdd();
    }
  }, [open, formType, box, form]);

  const initAdd = () => {
    form.resetFields();
    let boxLen = orderData?.boxes?.length;
    if (!!boxLen) {
      let tarBox = orderData!.boxes![boxLen - 1];
      form.setFieldsValue({
        boxNumber: undefined,
        lengthMm: tarBox.lengthMm,
        widthMm: tarBox.widthMm,
        heightMm: tarBox.heightMm,
        packageType: tarBox.packageType,
        marksNumbers: tarBox.marksNumbers,
        remark: tarBox.remark,
      });
    }
  }

  const handleSubmit = async (values: AddPackBox) => {
    try {
      if (formType === "ADD") {
        let newBox = await addBox({ ...values }, orderData?.id ?? '');
        if (newBox) onSuccess(newBox);
      } else {
        let originBox = { ...box };
        let changed: boolean = false;
        (Object.keys(values) as (keyof AddPackBox)[]).forEach((key) => {
          if (values[key] !== originBox?.[key]) changed = true;
        })
        if (changed) {
          const newBoxres = await editBox(values, originBox?.id ?? '');
          !!newBoxres && onSuccess({
            ...newBoxres,
            items: originBox?.items ?? []
          });
        } else onClose();
      }
    } catch (error) { }
  };

  return (
    <Modal width={600} maskClosable={false} centered
      title={formType === "ADD" ? t('warehouse.addBox') : t('warehouse.editBox', { no: box?.boxNumber })}
      open={open}
      onOk={form.submit}
      onCancel={onClose}
      okButtonProps={{ loading: loading === "BoxAdd" || loading === "BoxEdit" }}
      destroyOnClose
    >
      <Form form={form} onFinish={handleSubmit}>
        <Form.Item<AddPackBox>
          name="boxNumber"
          label={t('warehouse.boxNo')}
        >
          <Input placeholder={t('warehouse.boxNo')} />
        </Form.Item>
        <Form.Item<AddPackBox>
          name="lengthMm"
          label={t('warehouse.lengthMm')}
          rules={[{ required: true, message: t('warehouse.lengthMm') }]}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0} formatter={val => numFormmater(Number(val || 0))} placeholder={t('warehouse.lengthMm')} />
        </Form.Item>
        <Form.Item<AddPackBox>
          name="widthMm"
          label={t('warehouse.widthMm')}
          rules={[{ required: true, message: t('warehouse.widthMm') }]}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0} formatter={val => numFormmater(Number(val || 0))} placeholder={t('warehouse.widthMm')} />
        </Form.Item>
        <Form.Item<AddPackBox>
          name="heightMm"
          label={t('warehouse.heightMm')}
          rules={[{ required: true, message: t('warehouse.heightMm') }]}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0} formatter={val => numFormmater(Number(val || 0))} placeholder={t('warehouse.heightMm')} />
        </Form.Item>
        <Form.Item<AddPackBox> hidden
          name="grossWeightG"
          label={t('warehouse.grossWeightG')}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0} formatter={val => numFormmater(Number(val || 0))} placeholder={t('warehouse.grossWeightG')} />
        </Form.Item>
        <Form.Item<AddPackBox> hidden
          name="netWeightG"
          label={t('warehouse.netWeightG')}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0} formatter={val => numFormmater(Number(val || 0))} placeholder={t('warehouse.netWeightG')} />
        </Form.Item>
        <Form.Item<AddPackBox>
          name="packageType"
          label={t('warehouse.packageType')}
        >
          <SelectPackageType />
        </Form.Item>
        <Form.Item<AddPackBox>
          name="marksNumbers"
          label={t('warehouse.marksNumbers')}
        >
          <Input placeholder={t('warehouse.marksNumbers')} />
        </Form.Item>
        <Form.Item<AddPackBox>
          name="remark"
          label={t('order.remark')}
          layout="vertical"
          style={{ marginBottom: 80 }}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
};

export default BoxAddOrEdit;